import { visibleTags } from './mature-content'


export interface CuratedCollection {
  id: string
  name: string
  description: string
  // Search terms to find sounds for this collection (titles, tags, or keywords)
  searchTerms: string[]
  // Estimated sound count (for display before actual fetch)
  estimatedCount: number
  // Override SEO title (defaults to `${name} | Tesla Lock Sounds` if not set)
  seoTitle?: string
  // Shorter display name for H1 (defaults to name if not set)
  displayName?: string
  // Optional cover image path (e.g. /images/collections/<id>.webp)
  coverImage?: string
  // Optional accent token for hover effect (e.g. '--tls-c-mint')
  accentToken?: string
}

/**
 * Curated thematic collections that go beyond simple categories.
 * Each collection is defined by search terms that match sound titles/tags/descriptions.
 */
export const curatedCollections: CuratedCollection[] = [
  {
    id: 'gaming-nostalgia',
    name: 'Gaming Nostalgia',
    description: 'Classic video game sounds that\'ll make you smile',
    coverImage: '/images/collections/gaming-nostalgia.webp',
    accentToken: '--tls-c-mint',
    searchTerms: [
      'minecraft', 'roblox', 'mario', 'clash royale', 'fnaf', 'undertale',
      'csgo', 'cs:go', 'gta', 'fortnite', 'among us', 'amogus', 'zelda',
      'sonic', 'pokemon', 'pikachu', 'dota', 'valorant', 'geometry dash'
    ],
    estimatedCount: 45
  },
  {
    id: 'horror-suspense',
    name: 'Horror & Suspense',
    description: 'Spooky sounds to send chills down your spine',
    coverImage: '/images/collections/horror-suspense.webp',
    accentToken: '--tls-c-coral',
    searchTerms: [
      // Horror games
      'fnaf', 'jumpscare', 'phasmophobia', 'slender', 'backrooms', 'scary maze',
      // Horror movies/TV
      'pennywise', 'jeff the killer', 'jason', 'myers', 'michael myers',
      'demogorgon', 'vecna', 'stranger things', 'jaws',
      // Horror keywords (more specific than just "scream")
      'horror', 'scary', 'creepy', 'spooky', 'ghost', 'zombie',
      'haunted', 'nightmare', 'evil', 'sinister', 'terror', 'blood'
    ],
    estimatedCount: 25
  },
  {
    id: 'classic-tv-movies',
    name: 'Classic TV & Movies',
    description: 'Iconic sounds from your favorite shows and films',
    coverImage: '/images/collections/classic-tv-movies.webp',
    accentToken: '--tls-c-amber',
    searchTerms: [
      'star wars', 'darth vader', 'imperial march', 'breaking bad', 'heisenberg',
      'hector bell', 'jaws', 'spongebob', 'the office', 'law and order',
      'simpsons', 'nelson', 'curb', 'inception', 'x files', 'avengers',
      '20th century fox', 'john cena', 'wwe', 'dukes of hazzard', 'general lee'
    ],
    estimatedCount: 35
  },
  {
    id: 'anime-universe',
    name: 'Anime Universe',
    description: 'Epic sounds from the world of anime',
    coverImage: '/images/collections/anime-universe.webp',
    accentToken: '--tls-c-orchid',
    searchTerms: [
      'naruto', 'dattebayo', 'jojo', 'ayayay', 'dragon ball', 'goku',
      'jujutsu', 'gojo', 'domain expansion', 're:zero', 'anime', 'nani',
      'hadouken', 'gundam', 'one piece'
    ],
    estimatedCount: 20
  },
  {
    id: 'tech-retro',
    name: 'Tech & Retro Computing',
    description: 'Nostalgic tech sounds from the digital age',
    coverImage: '/images/collections/tech-retro.webp',
    accentToken: '--tls-c-teal',
    searchTerms: [
      // Operating systems
      'windows xp', 'windows 95', 'windows 98', 'mac os', 'linux',
      // Devices & apps
      'nokia', 'iphone', 'android', 'discord', 'skype', 'msn', 'aol',
      // Tech sounds
      'dial up', 'modem', 'dial-up', 'apple pay', 'usb', 'mii channel',
      'startup sound', 'error sound', 'system sound'
    ],
    estimatedCount: 30
  },
  {
    id: 'sports-arena',
    name: 'Sports Arena',
    description: 'The sounds of victory and competition',
    coverImage: '/images/collections/sports-arena.webp',
    accentToken: '--tls-c-amber',
    searchTerms: [
      'champions league', 'crowd', 'cheering', 'boxing bell', 'whistle',
      'nfl', 'f1', 'ronaldo', 'siuu', 'messi', 'goal', 'referee',
      'stadium', 'sports'
    ],
    estimatedCount: 25
  },
  {
    id: 'meme-legends',
    name: 'Meme Legends',
    description: 'The most iconic sounds from internet culture',
    coverImage: '/images/collections/meme-legends.webp',
    accentToken: '--tls-c-clay',
    searchTerms: [
      'bruh', 'vine boom', 'emotional damage', 'fbi open up', 'here we go again',
      'chipmunk laugh', 'sad violin', 'moments later', 'rizz', 'what da dog',
      'dun dun', 'damn son', 'airhorn', 'mlg', 'oof'
    ],
    estimatedCount: 40
  },
  {
    id: 'notification-alerts',
    name: 'Notification & Alerts',
    description: 'Perfect sounds for alerts and notifications',
    coverImage: '/images/collections/notification-alerts.webp',
    accentToken: '--tls-c-sky',
    searchTerms: [
      'notification', 'alert', 'discord', 'iphone', 'apple pay', 'instagram',
      'subscriber', 'buzzer', 'correct', 'wrong', 'ding', 'bell', 'chime'
    ],
    estimatedCount: 25
  },
  {
    id: 'celebration-hype',
    name: 'Celebration & Hype',
    description: 'Sounds to celebrate your wins',
    coverImage: '/images/collections/celebration-hype.webp',
    accentToken: '--tls-c-rose',
    searchTerms: [
      'cheering', 'clap', 'applause', 'happy new year', 'cash register',
      'ka-ching', 'here comes the money', 'achievement', 'yay', 'hooray',
      'celebration', 'victory', 'win'
    ],
    estimatedCount: 20
  },
  {
    id: 'scifi-classics',
    name: 'Sci-Fi Classics',
    description: 'Star Wars, Star Trek, Portal, Terminator: lock sounds from galaxies far, far away',
    coverImage: '/images/collections/scifi-classics.webp',
    accentToken: '--tls-c-mint',
    searchTerms: [
      'star wars', 'darth vader', 'imperial march', 'lightsaber', 'r2d2', 'r2-d2',
      'chewbacca', 'cantina', 'blaster', 'battle droid', 'roger roger',
      'star trek', 'phaser', 'self-destruct', 'door chime',
      'portal', 'glados', 'turret', 'sentry mode',
      'terminator', "i'll be back",
      'transformers', 'autobot', 'decepticon',
      'metal gear', 'codec',
      'wall-e', 'wall e',
      'squid game', 'dune', 'halo', 'jetsons',
      'prowler', 'spiderman 2099', 'spider-verse',
      'daisy bell', 'hal 9000',
      'pew pew', 'laser', 'futuristic', 'robot', 'sci-fi', 'cyberpunk'
    ],
    estimatedCount: 40
  },
  {
    id: 'jarvis-tesla-lock-sound',
    name: 'Jarvis & Iron Man',
    description: 'Suit up every time you lock: Jarvis voice lines, Iron Man suit-up, and arc reactor tones',
    coverImage: '/images/collections/jarvis-tesla-lock-sound.webp',
    accentToken: '--tls-c-amber',
    searchTerms: [
      'jarvis', 'iron man', 'tony stark', 'stark', 'marvel', 'avengers',
      'suit up', 'suit-up', 'arc reactor', 'welcome home', 'all systems online',
      'jarvis connected', 'defense protocol', 'friday', 'iron man jarvis',
    ],
    estimatedCount: 12,
    seoTitle: 'Jarvis Tesla Lock Sound: Free Iron Man WAV Downloads',
    displayName: 'Jarvis & Iron Man',
  },
  {
    id: 'tron-mode',
    name: 'Tesla Tron Mode Sounds',
    description: 'Download free Tesla Tron Mode lock, unlock & turn signal WAV files: works without the Easter egg. Instant USB install for Model 3, Y, S, X & Cybertruck.',
    searchTerms: [
      'tron', 'tron mode', 'tron legacy', 'cyberpunk', 'digital lock',
      'tesla tron', 'cybertruck digital', 'digital hum', 'digital chime',
      'electronic chime', 'electronic tick'
    ],
    estimatedCount: 8,
    seoTitle: 'Free Tesla Tron Mode Sounds: Lock, Unlock & Turn Signal WAV Downloads',
    displayName: 'Tron Mode',
    coverImage: '/images/collections/tron-mode.webp',
    accentToken: '--tls-c-sky'
  },
  {
    id: 'kids-family',
    name: 'Kids & Family Favorites',
    description: 'Bluey, Peppa Pig, SpongeBob, and the after-school-TV classics that make the whole family smile at the car',
    coverImage: '/images/collections/kids-channel-classics.webp',
    accentToken: '--tls-c-rose',
    searchTerms: [
      // Bluey (every existing Bluey sound is wholesome, safe as a broad term)
      'bluey',
      // Peppa Pig
      'peppa pig',
      // SpongeBob — specific, family-friendly beats only (not every meme in the catalog)
      'are you ready', 'i can do anything', 'spongebob bubble transition',
      'spongebob my leg', 'spongebob fail', 'a few moments later',
      // Toy Story
      'reach for the sky', 'to infinity and beyond',
      // Thomas & Friends
      'thomas the tank engine',
      // The Lego Movie (not "lego" alone — that also matches non-kid Lego SFX)
      'everything is awesome',
      // Big Hero 6
      'baymax',
      // Winnie the Pooh
      'pooh oh bother',
      // WALL-E
      'wall-e',
      // DuckTales
      'ducktales',
      // Dexter's Laboratory (theme only, not the Dee Dee meme clip)
      "dexter's laboratory",
      // Pokemon
      'pikachu',
      // Animal Crossing
      'isabelle',
      // Despicable Me
      'minion laughing',
      // Disney Channel
      'hannah montana',
      // The Magic School Bus
      'seatbelts everyone',
      // Nickelodeon / Cartoon Network idents
      'nickelodeon short intro',
      // Kids' internet classics
      'raining tacos', 'courage speak', 'duck toy', 'pikmin',
      // Paw Patrol — sourced under TES-1548 (Chase + Ryder catchphrases).
      // No CoComelon/Baby Shark term here on purpose: every sourceable clip for
      // those brands is the song itself, which the IP guardrail excludes, so the
      // term would match nothing forever.
      'paw patrol'
    ],
    estimatedCount: 46,
    seoTitle: 'Kids Tesla Lock Sounds: Free Bluey, Peppa Pig & SpongeBob WAV Downloads',
    displayName: 'Kids & Family',
  }
]

/**
 * Get a curated collection by ID
 */
export function getCuratedCollection(id: string): CuratedCollection | undefined {
  return curatedCollections.find(c => c.id === id)
}

/**
 * Check if a sound matches a curated collection's search terms
 */
export function soundMatchesCollection(
  sound: { title: string; description?: string | null; tags?: string[] | null },
  collection: CuratedCollection
): boolean {
  const searchableText = [
    sound.title.toLowerCase(),
    (sound.description || '').toLowerCase(),
    ...visibleTags(sound.tags).map(t => t.toLowerCase())
  ].join(' ')

  return collection.searchTerms.some(term =>
    searchableText.includes(term.toLowerCase())
  )
}

/**
 * Build a SQL ILIKE pattern for searching sounds in a collection
 */
export function buildCollectionSearchPattern(collection: CuratedCollection): string {
  // Returns patterns for use with Supabase's or() filter
  return collection.searchTerms
    .map(term => `title.ilike.%${term}%,description.ilike.%${term}%`)
    .join(',')
}
